/* eslint-disable max-classes-per-file */
import React from 'react';
import moment from 'moment';

import { ajaxFetch } from '~/helpers/api';
import { UserContext } from '~/dashboard/context';


/*
 * Formatter Mixin
 * Formats various pieces of PII for prettier output into the UI
 */
export const formatterMixin = (Base = Object) => class extends Base {
  static formatName(data = {}) {
    const {
      name_prefix: prefix = '',
      name_first: first = '',
      name_middle: middle = '',
      name_last: last = '',
      name_second_surname: surname = '',
      name_suffix: suffix = '',
    } = data;

    return `${prefix} ${first} ${middle} ${last} ${surname} ${suffix}`.replace(/\s+/g, ' ').trim();
  }


  static formatAddress(type, data = {}, className = '') {
    const keyPrefix = type === 'business' ? 'business_' : '';
    const line1Key = `${keyPrefix}address_line_1`;
    const line2Key = `${keyPrefix}address_line_2`;
    const cityKey = `${keyPrefix}address_city`;
    const stateKey = `${keyPrefix}address_state`;
    const zipKey = `${keyPrefix}address_postal_code`;
    const countryKey = `${keyPrefix}address_country_code`;

    let {
      [line1Key]: line1 = '',
      [line2Key]: line2 = '',
      [cityKey]: city = '',
      [stateKey]: state = '',
      [zipKey]: zip = '',
      [countryKey]: country = '',
    } = data;

    line1 = (line1 ? `${line1}${line2 ? ', ' : ''}` : '').toUpperCase();
    line2 = (line2 ? `${line2} ` : '').toUpperCase();
    city = (city ? `${city}, ` : '').toUpperCase();
    state = (state ? `${state} ` : '').toUpperCase();
    zip = (zip ? `${zip} ` : '').toUpperCase();
    country = (country ? `${country} ` : '').toUpperCase();

    const address = [];
    if (line1 || line2) address.push(`${line1}${line2}`.trim());
    if (city || state || zip || country) {
      address.push(`${city}${state}${zip}${country}`.trim());
    }

    return address.map(line => <p key={line} className={className}>{line}</p>);
  }


  static formatBusinessOwner(data) {
    return (data?.representatives ?? [])
      .filter(({ type }) => ['principal_owner', 'authorized_signer'].includes(type))
      .map(({ name_first: nameFirst, name_last: nameLast, type }) => (
        <p key={`${type}-${nameFirst}-${nameLast}`}>
          {`${nameFirst} ${nameLast}`}
        </p>
      ));
  }


  // NOTE:  we should deprecate this function in favor of a new util that utilizes the
  //        JS i18n API, or the existing jslib/formatter for phones
  static formatPhone(phone) {
    // Australia
    if (phone && phone.length === 12 && phone.slice(0, 3) === '+61') {
      return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6, 9)}-${phone.slice(9)}`;
    }
    // Legacy approach
    if (phone && phone.length === 12 && phone.charAt(0) === '+') {
      return `${phone.slice(2, 5)}-${phone.slice(5, 8)}-${phone.slice(8)}`;
    }
    // Base case
    return phone;
  }

  static formatSsn(ssn) {
    if (ssn && ssn.length === 9) {
      return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
    }
    return ssn;
  }

  static formatSsnMasked(ssn) {
    if (ssn && ssn.length === 9) {
      return `${'*'.repeat(3)}-${'*'.repeat(2)}-${ssn.slice(5)}`;
    }
    return ssn;
  }

  static formatBirthDate(birthDate) {
    let formatted = birthDate;
    if (birthDate) {
      if (typeof formatted === 'number') formatted = String(formatted);
      formatted = moment(formatted.split('T')[0]).format('MMM Do YYYY');
    }
    if (formatted === 'Invalid date') {
      formatted = birthDate;
    }
    return formatted;
  }
};


/*
 * Permissions Mixin
 * Parses, stores and checks Agent Permissions output into the
 * HTML templates from the back end.
 */
export const permissionsMixin = Base => class extends Base {
  static userContext = UserContext

  constructor(props, context) {
    super(props, context);

    this.setPermissions(props.permissions);
  }

  componentWillUpdate({ userContext }) {
    if (Object.keys(this.permissions).length) return; // will not subscribe a pre-defined permission
    if (userContext?.permissions && userContext.permissions !== this.permissions) {
      this.setPermissions(userContext.permissions);
    }
  }


  setPermissions(permissions = false) {
    if (permissions) this.permissions = permissions;
    else if (!{}.hasOwnProperty.call(this, 'permissions')) {
      if (document.getElementById('json-perms')) {
        this.permissions = JSON.parse(document.getElementById('json-perms').value);
      } else if (Object.prototype.hasOwnProperty.call(this, 'context')
                 && this.context
                 && Object.prototype.hasOwnProperty.call(this.context, 'permissions')) {
        this.permissions = this.context.permissions;
      } else {
        this.permissions = {};
      }
    }
  }


  checkPermission(key, permissionData) {
    const permissions = permissionData || this.permissions;
    if ({}.hasOwnProperty.call(permissions, key)) return permissions[key];
    return false;
  }
};


/*
 * Service Response Mixin
 * Handles querying and caching for ServiceResponseModal service responses,
 * which are pulled conditionally from the back end when the modal opens
 * instead of when the page loads to improve performance.
 */
export const serviceResponseMixin = Base => class extends Base {
  constructor(props) {
    super(props);
    this.updateSelectedServiceResponse = this.updateSelectedServiceResponse.bind(this);
    this.state = {
      selectedServiceResponse: 0,
      serviceResponses: {},
    };
  }


  updateSelectedServiceResponse(selectedServiceResponse, url = false) {
    const serviceResponses = { ...this.state.serviceResponses };
    this.setState({ selectedServiceResponse }, () => {
      if (typeof selectedServiceResponse === 'number') {
        if (selectedServiceResponse && !serviceResponses[selectedServiceResponse] && url) {
          ajaxFetch(url).then(data => {
            serviceResponses[selectedServiceResponse] = data.service_response;
            this.setState({ serviceResponses });
          });
        }
      }
    });
  }
};
